import { useDispatch, useSelector } from 'react-redux';

import { useRouter } from 'hooks/useRouter';
import { emergentModal } from 'store/features';
import { AnyRecord, ModalId, ModalWindowProps } from 'types';
type OnCloseFn = () => void;
type PushModal = <Id extends ModalId>(
  id: Id,
  props: ModalWindowProps<Id>,
  options?: {
    timeout?: number;
    emergent?: boolean;
  }
) => void;

type OnIsOpen = <Id extends ModalId>(id: Id) => boolean;

export const useModal = (): {
  pushModal: PushModal;
  onClose: OnCloseFn;
  onCloseAll: OnCloseFn;
  onIsOpen: OnIsOpen;
  modalId?: ModalId;
  modalProps?: AnyRecord;
} => {
  const { params, removeParams, updateParams } = useRouter();
  const { modalId, modalProps } = params;

  const dispatch = useDispatch();
  const emergentState = useSelector(emergentModal.selectors.getState);

  const onClose: OnCloseFn = () => {
    const handleCloseLastEmergent = () => dispatch(emergentModal.actions.pop());
    const handleCloseModal = () => removeParams(['modalProps', 'modalId']);

    const hasSomeEmergent = !!emergentState.length;
    (hasSomeEmergent ? handleCloseLastEmergent : handleCloseModal)();
  };

  const onCloseAll: OnCloseFn = () => {
    const handleCloseAllEmergent = () => dispatch(emergentModal.actions.reset());
    const handleCloseModal = () => removeParams(['modalProps', 'modalId']);

    handleCloseAllEmergent();
    handleCloseModal();
  };

  const pushModal: PushModal = (modalId, props, options) => {
    const { timeout, emergent } = options || {};

    const handleAddEmergent = () => {
      dispatch(emergentModal.actions.push({ id: modalId, props }));
    };

    const handlePushModal = () => {
      updateParams({ modalId, modalProps: JSON.stringify(props) });
    };

    const handlePush = emergent ? handleAddEmergent : handlePushModal;

    if (timeout) {
      setTimeout(handlePush, timeout);
      return;
    }
    handlePush();
  };

  const onIsOpen: OnIsOpen = (currentId) =>
    emergentState.some(({ id }) => currentId === id) || modalId === currentId;

  return {
    pushModal,
    onClose,
    onCloseAll,
    onIsOpen,
    modalId: modalId as ModalId | undefined,
    modalProps: modalProps ? JSON.parse(modalProps as string) : undefined,
  };
};
